import React from 'react';
import { Controller } from 'react-hook-form';
import { observer } from 'mobx-react';
import Input from '@alfa-retail-react/components/input';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { WEB_VISOR_DISABLE } from 'app/utils/AnalyticsUtils';
import { OrderFormValues } from 'app/logic/pages/order/OrderPage';
import { OrderFormStore } from 'app/logic/pages/order/OrderFormStore';
import { ValidationRules } from 'app/form/ValidationRules';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';

export interface SecretWordFormItemProps {
    /**
     * Хранилище состояния страницы ввода личных данных.
     */
    formStore: OrderFormStore;
}

const PLACEHOLDER = 'Придумайте кодовое слово';
const HINT =
    'Нужно для идентификации при обращении в банк дистанционно (девичья фамилия матери и др.)';

const ALLOWED_CHARACTERS_PATTERN =
    /^[A-Za-zА-Яа-яЁё0-9][A-Za-zА-Яа-яЁё0-9 `'"\-]*[A-Za-zА-Яа-яЁё0-9`'"\\-]$/;
const ALLOWED_CHARACTERS_VALIDATION_ERROR_MESSAGE =
    'Допустимые символы: буквы кириллицей (А, б, в), латиницей (A, b, c), цифры, дефис, пробел, апостроф, кавычка, должно начинаться буквой или цифрой';

const MIN_LENGTH = 2;
const MAX_LENGTH = 15;
const MIN_MAX_LENGTH_VALIDATION_ERROR_MESSAGE =
    `Допустимое количество символов – от ${MIN_LENGTH} до ${MAX_LENGTH}`;

const SECRET_WORD_FORM_ITEM_NAME = 'secretWord';

/**
 * Компонент ввода секретного слова.
 */
export const SecretWordFormItem = observer(
    ({ formStore }: SecretWordFormItemProps) => {
        const { control, trigger, handleMultipleErrors, setFieldElement } =
            useFormContext<OrderFormValues>();
        const fieldHandler = new InputFieldEventHandler<OrderFormValues>(
            trigger,
        );

        return (
            <Controller<OrderFormValues>
                control={control}
                defaultValue={formStore.secretWord ?? ''}
                name={SECRET_WORD_FORM_ITEM_NAME}
                rules={{
                    required: ValidationRules.required(),
                    minLength: ValidationRules.minLength(
                        MIN_LENGTH,
                        MIN_MAX_LENGTH_VALIDATION_ERROR_MESSAGE,
                    ),
                    maxLength: ValidationRules.maxLength(
                        MAX_LENGTH,
                        MIN_MAX_LENGTH_VALIDATION_ERROR_MESSAGE,
                    ),
                    pattern: ValidationRules.allowedCharacters(
                        ALLOWED_CHARACTERS_PATTERN,
                        ALLOWED_CHARACTERS_VALIDATION_ERROR_MESSAGE,
                    ),
                }}
                render={({ field, fieldState }) => {
                    const { onBlur, onClear, onChange } =
                        fieldHandler.getEventHandlers(
                            field,
                            SECRET_WORD_FORM_ITEM_NAME,
                            formStore.setSecretWord,
                            false,
                        );

                    return (
                        <Input
                            clear
                            shouldUpperCase
                            inputClassName={WEB_VISOR_DISABLE}
                            ref={(element) => {
                                field.ref(element);
                                setFieldElement(
                                    SECRET_WORD_FORM_ITEM_NAME,
                                    element,
                                );
                            }}
                            size="l"
                            value={field.value as string}
                            label={PLACEHOLDER}
                            hint={HINT}
                            error={handleMultipleErrors(fieldState.error)}
                            onBlur={onBlur}
                            onClear={onClear}
                            onChange={onChange}
                        />
                    );
                }}
            />
        );
    },
);
